// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import Layout from "../components/layout/Layout";
import AdminLayout from "../components/layout/AdminLayout";
import LandingPage from "../pages/LandingPage";
import LoginPage from "../pages/auth/LoginPage";
import RegisterPage from "../pages/auth/RegisterPage";
import AdminDashboard from "../pages/admin/AdminDashboard";
import Bookings from "../pages/admin/Bookings";
import UserDashboard from "../pages/user/UserDashboard";
import UserBookings from "../pages/user/UserBookings";
import AutomatisierungSettings from "../pages/admin/AutomatisierungSettings";
import WorkflowStatus from "../pages/admin/WorkflowStatus";
import TenantManagement from "../pages/admin/TenantManagement";
import LocationsManagement from "../pages/admin/LocationsManagement";
import PlansManagement from "../pages/admin/PlansManagement";
import StandorteListView from "../pages/admin/StandorteListView";
import BlockAvailability from "../pages/admin/BlockAvailability";
import AvailabilityViewPreview from "../pages/admin/AvailabilityViewPreview";
import FinancialForecast from "../pages/admin/FinancialForecast";
import Products from "../pages/admin/Products";
import {
  ROLE_CATEGORIES,
  hasAdminAccess,
  hasRoleCategory,
  MAP_EDITOR_ROLES,
} from "../config/roleConfig";
import GDPRAdminInterface from "../pages/admin/GDPRAdminInterface";
import BrandingKontaktSettings from "../pages/admin/BrandingKontaktSettings";
import BuchungenSettings from "../pages/admin/BuchungenSettings";
import LokalisierungSettings from "../pages/admin/LokalisierungSettings";
import PerformanceReports from "../pages/admin/PerformanceReports";
import LLMUsageReports from "../pages/admin/LLMUsageReports";
import APIAnalyticsReports from "../pages/admin/APIAnalyticsReports";
import ScenarioEditor from "../pages/admin/ScenarioEditor";
import ScenarioManagement from "../pages/admin/ScenarioManagement";
import SpacesList from "../pages/admin/SpacesList";
import {
  Activity,
  BarChart3,
  Building,
  Building2,
  Calendar,
  ChartPie,
  ClipboardCheck,
  Clock,
  Code,
  CreditCard,
  DollarSign,
  Languages,
  Layers,
  LayoutGrid,
  Map,
  MapPin,
  Package,
  Palette,
  Settings,
  Workflow,
  Zap,
} from "lucide-react";
import UserList from "../components/admin/UserList";
import RoleManagement from "../components/admin/RoleManagement";
import Permission from "../components/admin/Permission";

// Route types
export const ROUTE_TYPES = {
  PUBLIC: "public",
  PROTECTED: "protected",
  ADMIN: "admin",
  USER: "user",
};

// Helper function to create route permissions
const createRoutePermissions = (permissions) => {
  return Array.isArray(permissions) ? permissions : [permissions];
};

// Helper function to get the first accessible route for a user
export const getFirstAccessibleRoute = (userRoles = []) => {
  if (!userRoles || userRoles.length === 0) {
    return "/";
  }

  // Use centralized role checking
  if (hasAdminAccess(userRoles)) {
    return "/admin/bookings";
  }

  // Check if user has customer/user roles
  if (hasRoleCategory(userRoles, "USER_ROLES")) {
    return "/user/dashboard";
  }

  return "/";
};

export const routes = [
  // ── Public ──────────────────────────────────────────────────
  {
    path: "/",
    element: LandingPage,
    type: ROUTE_TYPES.PUBLIC,
    title: "Home",
    exact: true,
  },
  {
    path: "/login",
    element: LoginPage,
    type: ROUTE_TYPES.PUBLIC,
    title: "Login",
    titleKey: "routes.login",
    redirectIfAuthenticated: "/admin/bookings",
  },
  {
    path: "/register",
    element: RegisterPage,
    type: ROUTE_TYPES.PUBLIC,
    title: "Register",
    titleKey: "routes.register",
    redirectIfAuthenticated: "/admin/bookings",
  },

  // ── 1. Operations ───────────────────────────────────────────
  {
    path: "/admin/bookings",
    element: Bookings,
    layout: AdminLayout,
    type: ROUTE_TYPES.ADMIN,
    title: "Operations",
    titleKey: "routes.operations",
    icon: LayoutGrid,
    roles: [
      ...ROLE_CATEGORIES.ADMIN_ROLES,
      ...ROLE_CATEGORIES.MANAGER_ROLES,
      ...ROLE_CATEGORIES.OPERATIONAL_ROLES,
      ...ROLE_CATEGORIES.SUPER_ADMIN_ROLES,
    ],
    children: [
      {
        path: "/admin/bookings",
        element: Bookings,
        layout: AdminLayout,
        type: ROUTE_TYPES.ADMIN,
        title: "Buchungen",
        titleKey: "routes.bookingsManagement",
        icon: Calendar,
        permissions: createRoutePermissions(["booking.read", "bookings.view"]),
      },
      {
        path: "/admin/availability-view",
        element: AvailabilityViewPreview,
        layout: AdminLayout,
        type: ROUTE_TYPES.ADMIN,
        title: "Verfügbarkeit",
        titleKey: "routes.availability",
        icon: Clock,
        permissions: createRoutePermissions(["products.view", "scenario.read"]),
      },
      {
        path: "/admin/workflow-status",
        element: WorkflowStatus,
        layout: AdminLayout,
        type: ROUTE_TYPES.ADMIN,
        title: "Workflow Status",
        titleKey: "routes.workflowStatus",
        icon: Workflow,
        permissions: createRoutePermissions(["system.admin", "system.view"]),
      },
      {
        path: "/admin/plans",
        element: PlansManagement,
        layout: AdminLayout,
        type: ROUTE_TYPES.ADMIN,
        title: "Pläne",
        titleKey: "routes.plans",
        icon: CreditCard,
        permissions: createRoutePermissions(["system.admin", "system.view"]),
      },
      {
        path: "/admin/business-metrics",
        element: AdminDashboard,
        layout: AdminLayout,
        type: ROUTE_TYPES.ADMIN,
        title: "Business Metrics",
        titleKey: "routes.businessMetrics",
        icon: BarChart3,
        permissions: createRoutePermissions([
          "report.view",
          "reports.sales",
          "reports.inventory",
          "reports.customers",
          "reports.bookings",
        ]),
      },
      {
        path: "/admin/performance",
        element: PerformanceReports,
        layout: AdminLayout,
        type: ROUTE_TYPES.ADMIN,
        title: "System Performance",
        titleKey: "routes.systemPerformance",
        icon: Activity,
        permissions: createRoutePermissions([
          "report.view",
          "reports.sales",
          "reports.inventory",
          "reports.customers",
          "reports.bookings",
        ]),
      },
      {
        path: "/admin/llm-usage",
        element: LLMUsageReports,
        layout: AdminLayout,
        type: ROUTE_TYPES.ADMIN,
        title: "LLM Operations",
        titleKey: "routes.llmOperations",
        icon: ChartPie,
        permissions: createRoutePermissions([
          "report.view",
          "reports.sales",
          "reports.inventory",
          "reports.customers",
          "reports.bookings",
        ]),
      },
      {
        path: "/admin/api-analytics",
        element: APIAnalyticsReports,
        layout: AdminLayout,
        type: ROUTE_TYPES.ADMIN,
        title: "API Usage",
        icon: Code,
        permissions: createRoutePermissions([
          "report.view",
          "reports.sales",
          "reports.inventory",
          "reports.customers",
          "reports.bookings",
        ]),
      },
    ],
  },

  // ── 2. Financial Analytics (standalone) ─────────────────────
  {
    path: "/admin/financial-forecast",
    element: FinancialForecast,
    layout: AdminLayout,
    type: ROUTE_TYPES.ADMIN,
    title: "Financial Analytics",
    icon: DollarSign,
    permissions: createRoutePermissions(["report.view", "reports.view"]),
  },

  // ── 3. Settings ─────────────────────────────────────────────
  {
    path: "/admin/branding-kontakt",
    element: BrandingKontaktSettings,
    layout: AdminLayout,
    type: ROUTE_TYPES.ADMIN,
    title: "Settings",
    icon: Settings,
    roles: ROLE_CATEGORIES.ADMIN_ROLES,
    permissions: createRoutePermissions(["system.admin", "system.view"]),
    children: [
      {
        path: "/admin/branding-kontakt",
        element: BrandingKontaktSettings,
        layout: AdminLayout,
        type: ROUTE_TYPES.ADMIN,
        title: "Branding & Kontakt",
        titleKey: "routes.brandingKontakt",
        icon: Palette,
        permissions: createRoutePermissions([
          "tenant_settings.view",
          "tenant_settings.manage",
        ]),
      },
      {
        path: "/admin/buchungen-settings",
        element: BuchungenSettings,
        layout: AdminLayout,
        type: ROUTE_TYPES.ADMIN,
        title: "Buchungsregeln",
        icon: ClipboardCheck,
        permissions: createRoutePermissions([
          "tenant_settings.view",
          "tenant_settings.manage",
        ]),
      },
      {
        path: "/admin/lokalisierung-settings",
        element: LokalisierungSettings,
        layout: AdminLayout,
        type: ROUTE_TYPES.ADMIN,
        title: "Lokalisierung",
        titleKey: "routes.lokalisierungSettings",
        icon: Languages,
        permissions: createRoutePermissions([
          "tenant_settings.view",
          "tenant_settings.manage",
        ]),
      },
      {
        path: "/admin/workflow-settings",
        element: AutomatisierungSettings,
        layout: AdminLayout,
        type: ROUTE_TYPES.ADMIN,
        title: "Automatisierung",
        icon: Zap,
        permissions: createRoutePermissions([
          "tenant_settings.view",
          "tenant_settings.manage",
          "system.admin",
          "system.view",
        ]),
      },
      {
        path: "/admin/standorte",
        element: LocationsManagement,
        layout: AdminLayout,
        type: ROUTE_TYPES.ADMIN,
        title: "Standorte",
        titleKey: "routes.standorte",
        icon: MapPin,
        permissions: createRoutePermissions(["products.view", "scenario.read"]),
      },
      {
        path: "/admin/products",
        element: Products,
        layout: AdminLayout,
        type: ROUTE_TYPES.ADMIN,
        title: "Produkte",
        titleKey: "routes.products",
        icon: Package,
        permissions: createRoutePermissions(["products.view"]),
      },
      {
        path: "/admin/scenario-management",
        element: ScenarioManagement,
        layout: AdminLayout,
        type: ROUTE_TYPES.ADMIN,
        title: "Spaces (Map Editor)",
        icon: Layers,
        permissions: createRoutePermissions(["products.view", "scenario.read"]),
      },
    ],
  },

  // ── 4. Mandanten Management ─────────────────────────────────
  {
    path: "/admin/mandanten-management",
    element: TenantManagement,
    layout: AdminLayout,
    type: ROUTE_TYPES.ADMIN,
    title: "Mandanten Management",
    titleKey: "routes.tenantsManagement",
    icon: Building,
    roles: ROLE_CATEGORIES.SUPER_ADMIN_ROLES,
    permissions: createRoutePermissions(["products.view", "scenario.read"]),
    children: [
      {
        path: "/admin/mandanten-management",
        element: TenantManagement,
        layout: AdminLayout,
        type: ROUTE_TYPES.ADMIN,
        title: "Mandanten",
        titleKey: "routes.tenants",
        icon: Building2,
        permissions: createRoutePermissions(["products.view", "scenario.read"]),
      },
      {
        path: "/admin/standorte-uebersicht",
        element: StandorteListView,
        layout: AdminLayout,
        type: ROUTE_TYPES.ADMIN,
        title: "Standorte",
        titleKey: "routes.standorte",
        icon: MapPin,
        permissions: createRoutePermissions(["products.view", "scenario.read"]),
      },
      {
        path: "/admin/scenarios",
        element: ScenarioManagement,
        layout: AdminLayout,
        type: ROUTE_TYPES.ADMIN,
        title: "Szenarien",
        icon: Map,
        permissions: createRoutePermissions(["products.view", "scenario.read"]),
      },
      {
        path: "/admin/spaces",
        element: SpacesList,
        layout: AdminLayout,
        type: ROUTE_TYPES.ADMIN,
        title: "Flächen",
        titleKey: "routes.spacesList",
        icon: LayoutGrid,
        permissions: createRoutePermissions(["system.admin", "system.view"]),
      },
    ],
  },

  // ── Hidden admin routes (URL-accessible, not in sidebar) ────
  {
    path: "/admin/scenarios",
    element: BlockAvailability,
    layout: AdminLayout,
    type: ROUTE_TYPES.ADMIN,
    permissions: createRoutePermissions(["products.view", "scenario.read"]),
  },
  {
    path: "/admin/users",
    element: UserList,
    layout: AdminLayout,
    type: ROUTE_TYPES.ADMIN,
    permissions: createRoutePermissions(["user.read", "users.view"]),
  },
  {
    path: "/admin/roles",
    element: RoleManagement,
    layout: AdminLayout,
    type: ROUTE_TYPES.ADMIN,
    roles: ROLE_CATEGORIES.SUPER_ADMIN_ROLES,
    permissions: createRoutePermissions(["role.read", "roles.view"]),
  },
  {
    path: "/admin/permissions",
    element: Permission,
    layout: AdminLayout,
    type: ROUTE_TYPES.ADMIN,
    roles: ROLE_CATEGORIES.SUPER_ADMIN_ROLES,
  },
  {
    path: "/admin/compliance-settings",
    element: GDPRAdminInterface,
    layout: AdminLayout,
    type: ROUTE_TYPES.ADMIN,
    permissions: createRoutePermissions(["gdpr.audit", "gdpr.manage"]),
  },
  {
    path: "/admin/scenario-editor",
    element: ScenarioEditor,
    layout: AdminLayout,
    type: ROUTE_TYPES.ADMIN,
    // Map Editor: Super Admin, Tenant Admin, Manager — not Operators.
    roles: MAP_EDITOR_ROLES,
  },
  {
    path: "/admin/scenario-editor/:scenarioId",
    element: ScenarioEditor,
    layout: AdminLayout,
    type: ROUTE_TYPES.ADMIN,
    // Map Editor: Super Admin, Tenant Admin, Manager — not Operators.
    roles: MAP_EDITOR_ROLES,
  },

  // ── Protected user routes ───────────────────────────────────
  {
    path: "/user/dashboard",
    element: UserDashboard,
    layout: Layout,
    type: ROUTE_TYPES.USER,
    title: "User Dashboard",
    titleKey: "routes.userDashboard",
    roles: ROLE_CATEGORIES.USER_ROLES,
  },
  {
    path: "/user/bookings",
    element: UserBookings,
    layout: Layout,
    type: ROUTE_TYPES.USER,
    title: "My Bookings",
    titleKey: "routes.myBookings",
    roles: ROLE_CATEGORIES.USER_ROLES,
  },

  // ── Redirects ───────────────────────────────────────────────
  {
    path: "/dashboard",
    type: ROUTE_TYPES.PROTECTED,
    redirect: "/admin/bookings",
  },
  {
    path: "/admin/branding",
    type: ROUTE_TYPES.ADMIN,
    redirect: "/admin/branding-kontakt",
  },
  {
    path: "/admin/kontakt",
    type: ROUTE_TYPES.ADMIN,
    redirect: "/admin/branding-kontakt",
  },
  {
    path: "/404",
    type: ROUTE_TYPES.PUBLIC,
    redirect: "/",
  },
  {
    path: "*",
    type: ROUTE_TYPES.PUBLIC,
    redirect: "/",
  },
];

// Helper functions
export const getPublicRoutes = () => {
  return routes.filter((route) => route.type === ROUTE_TYPES.PUBLIC);
};

export const getProtectedRoutes = () => {
  return routes.filter(
    (route) =>
      route.type === ROUTE_TYPES.PROTECTED ||
      route.type === ROUTE_TYPES.ADMIN ||
      route.type === ROUTE_TYPES.USER,
  );
};

export const getAdminRoutes = () => {
  return routes.filter((route) => route.type === ROUTE_TYPES.ADMIN);
};

export const getRoutesByRole = (userRoles = []) => {
  return routes.filter((route) => {
    if (route.type === ROUTE_TYPES.PUBLIC) return true;
    if (!route.roles) return true;
    return route.roles.some((role) => userRoles.includes(role));
  });
};

export const getRoutesByPermissions = (userPermissions = []) => {
  return routes.filter((route) => {
    if (route.type === ROUTE_TYPES.PUBLIC) return true;
    if (!route.permissions) return true;
    return route.permissions.some((permission) =>
      userPermissions.includes(permission),
    );
  });
};

export const findRouteByPath = (path) => {
  return routes.find((route) => route.path === path);
};
